import get from 'lodash/get';
import { createSelector } from 'reselect';

export const selectDeepLinkTab = state =>
  get(state, 'routing.locationBeforeTransitions.query.tab', '');

export const selectDeepLinkDependentId = state =>
  get(state, 'routing.locationBeforeTransitions.query.dependent_id', '');

export const selectLocationBeforeTransitions = state =>
  get(state, 'routing.locationBeforeTransitions', {});

export const selectLocationBeforeTransitionsQuery = state =>
  get(state, 'routing.locationBeforeTransitions.query', {});

export const selectPatientPortalRootState = state =>
  get(state, 'healthboard.patientPortal');

export const selectMedicationsEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'medications.list', []),
);

export const selectMedicationsTotal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'medications.total', 0),
);

export const selectMedicationsLoading = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'medications.sending', false),
);

export const selectMedicationsPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'medications.sending', false),
);

export const selectMedicationsRefillsHistoryList = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.refillsHistory.list', []),
);

export const selectMedicationsRefillsHistoryPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.refillsHistory.sending', false),
);

export const selectRefillsAvailableMedicationsList = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.refillsAvailableMedications.list', []),
);

export const selectRefillsAvailableMedicationsPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.refillsAvailableMedications.sending', false),
);

export const selectMedicationRefillsRequestPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.medicationRefillRequest.sending', false),
);

export const selectMedicationRefillsRequestError = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.medicationRefillRequest.error'),
);

export const selectMedicationRefillsUnavailablePharmacyError = createSelector(
  selectMedicationRefillsRequestError,
  error => get(error, 'code', '') === 'unprocessible_entity',
);

export const selectAllergiesEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'allergies.list', []),
);

export const selectAllergiesPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'allergies.sending', false),
);

export const selectImmunizationEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'immunizations.list', []),
);

export const selectImmunizationPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'immunizations.sending', false),
);

export const selectConditionsEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'conditions.list', []),
);

export const selectConditionsPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'conditions.sending', false),
);

export const selectFamilyHistoryEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'familyHistory.list', []),
);

export const selectFamilyHistoryPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'familyHistory.sending', false),
);

export const selectSocialHistoryEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'socialHistory.list', []),
);

export const selectSocialHistoryPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'socialHistory.sending', false),
);

export const selectEncountersEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'encounters.list', []),
);

export const selectEncountersPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'encounters.sending', false),
);

export const selectProceduresEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'procedures.list', []),
);

export const selectProceduresPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'procedures.sending', false),
);

export const selectActivityLogEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'activityLog.list', []),
);

export const selectActivityLogPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'activityLog.sending', false),
);

export const selectActivityLogEntityOffset = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'activityLog.offset', 0),
);

export const selectActivityLogEntityIsLastPage = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'activityLog.isLastPage', false),
);

export const selectHealthRecordEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'healthRecord.list', []),
);

export const selectHealthRecordPending = createSelector(
  selectPatientPortalRootState,
  state => {
    const sending = get(state, 'healthRecord.sending', false);
    const isPagination = get(state, 'healthRecord.isPagination', false);
    return !isPagination && sending;
  },
);

export const selectHealthRecordPaginationPending = createSelector(
  selectPatientPortalRootState,
  state => {
    const sending = get(state, 'healthRecord.sending', false);
    const isPagination = get(state, 'healthRecord.isPagination', false);
    return isPagination && sending;
  },
);

export const selectHealthRecordSucceed = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'healthRecord.succeed', false),
);

export const selectSetPreferredPharmacySuccess = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'refills.setPreferredPharmacy.succeed', false),
);

export const selectHealthRecordEntityOffset = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'healthRecord.offset', 0),
);

export const selectHealthRecordEntityTotal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'healthRecord.total', false),
);

export const selectLabTestResultsEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labTestResults.list', []),
);

export const selectLabTestResultsPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labTestResults.sending', false),
);

export const selectPatientChannelsForShare = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'patientChannelsForShare.list', []),
);

export const selectPatientChannelsForSharePending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'patientChannelsForShare.sending', false),
);

export const selectSocialChannelsForShare = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'socialChannelsForShare.list', []),
);

export const selectSocialChannelsForSharePending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'socialChannelsForShare.sending', false),
);

export const selectShareToChatPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'shareToChat.sending', false),
);

export const selectOpenedResultsDetailsModal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetailsModal.opened', false),
);

export const selectOpenedLabResultHistoryModal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryModal.opened', false),
);

export const selectLabResultHistoryData = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryRequest.list', []),
);

export const selectLabResultHistoryPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryRequest.sending', false),
);

export const selectLabResultHistoryError = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryRequest.error', ''),
);

export const selectTestResultComponentCode = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryModal.componentCode', ''),
);

export const selectTestResultComponentName = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'labResultHistoryModal.name', ''),
);

export const shareToChatModal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'shareToChatModal.opened', false),
);

export const shareToChatModalDocumentUid = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'shareToChatModal.documentUid', ''),
);

export const shareToEmailModal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'shareToEmailModal.opened', false),
);

export const shareToEmailModalDocumentUid = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'shareToEmailModal.documentUid', ''),
);

export const chatMembersModalOpened = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'chatMembersModal.opened', false),
);

export const chatMembersModalChannelName = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'chatMembersModal.chatRoomName', ''),
);

export const chatMembersModalList = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'chatMembersModal.memberList', []),
);

export const chatMembersModalDate = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'chatMembersModal.date', ''),
);

export const selectIdResultsDetailsModal = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetailsModal.ccdLabTestId', ''),
);

export const selectResultsDetailsModalEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetailsModal.entity', {}),
);

export const selectResultsDetailsEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetails.ccdUserLabResults', []),
);

export const selectResultsDocument = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetails.ccdaUserDocuments'),
);

export const selectResultsDetailsPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'resultsDetails.sending', false),
);

export const selectAvailableCategoriesEntity = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'availableCategories.list', []),
);

export const selectAvailableCategoriesPending = createSelector(
  selectPatientPortalRootState,
  state => get(state, 'availableCategories.sending', false),
);

export const combinedPendingDataRequest = createSelector(
  selectAllergiesPending,
  selectMedicationsPending,
  selectImmunizationPending,
  selectConditionsPending,
  selectLabTestResultsPending,
  selectFamilyHistoryPending,
  selectEncountersPending,
  selectSocialHistoryPending,
  selectHealthRecordPending,
  selectActivityLogPending,
  selectProceduresPending,
  (
    allergiesPending,
    medicationsPending,
    immunizationPending,
    conditionsPending,
    labTestResultsPending,
    familyHistoryPending,
    encountersPending,
    socialHistoryPending,
    healthRecordPending,
    activityLogPending,
    proceduresPending,
  ) =>
    allergiesPending ||
    medicationsPending ||
    immunizationPending ||
    conditionsPending ||
    labTestResultsPending ||
    familyHistoryPending ||
    encountersPending ||
    socialHistoryPending ||
    healthRecordPending ||
    activityLogPending ||
    proceduresPending,
);



// WEBPACK FOOTER //
// ./src/redux/healthboard/patientPortal/selectors.js